import React, { useMemo, useState } from "react";
import EmptyState from "./EmptyState";
import LoadingState from "./LoadingState";

const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);

/**
 * DataTable — premium table wrapper with optional search + pagination + states.
 * Pure presentational; consumers pass `data` (array) and `columns` config.
 *
 * columns: [{ key, header, render?, className?, align?: 'left'|'right'|'center', searchable?: bool }]
 */
const DataTable = ({
  title,
  subtitle,
  columns = [],
  data = [],
  loading = false,
  emptyTitle = "No results",
  emptyDescription,
  emptyAction,
  toolbar,
  search = true,
  searchPlaceholder = "Search…",
  pageSize = 10,
  paginate = true,
  rowKey = (row, i) => row?.id ?? i,
  onRowClick,
  className = "",
  stickyHeader = true,
  footer,
}) => {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const searchable = useMemo(
    () => columns.filter((c) => c.searchable !== false),
    [columns]
  );

  const filtered = useMemo(() => {
    if (!query) return data;
    const q = query.toLowerCase();
    return data.filter((row) =>
      searchable.some((col) => {
        const v = row?.[col.key];
        if (v == null) return false;
        return String(v).toLowerCase().includes(q);
      })
    );
  }, [data, query, searchable]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const view = paginate ? filtered.slice(start, start + pageSize) : filtered;

  const pages = useMemo(() => {
    const arr = [];
    const max = 5;
    let s = Math.max(1, safePage - 2);
    let e = Math.min(totalPages, s + max - 1);
    s = Math.max(1, e - max + 1);
    for (let i = s; i <= e; i++) arr.push(i);
    return arr;
  }, [safePage, totalPages]);

  return (
    <div className={`dt-shell ${className}`}>
      {(title || subtitle || search || toolbar) && (
        <div className="dt-toolbar">
          <div>
            {title && <h3 className="dt-toolbar__title">{title}</h3>}
            {subtitle && <p className="t-body-sm t-muted" style={{ margin: 0 }}>{subtitle}</p>}
          </div>
          <div className="dt-toolbar__actions">
            {search && (
              <label className="dt-search">
                <IconSearch />
                <input
                  type="search"
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                />
              </label>
            )}
            {toolbar}
          </div>
        </div>
      )}

      <div className="dt-scroll">
        {loading ? (
          <LoadingState />
        ) : view.length === 0 ? (
          <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
        ) : (
          <table className="dt-table" style={stickyHeader ? undefined : { /* allow override */ }}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={col.className || ""}
                    style={{ textAlign: col.align || "left", width: col.width }}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {view.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={col.cellClassName || ""}
                      style={{ textAlign: col.align || "left" }}
                    >
                      {col.render ? col.render(row, i) : row?.[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {paginate && !loading && filtered.length > pageSize && (
        <div className="dt-foot">
          <span>
            Showing <strong>{start + 1}</strong>–<strong>{Math.min(start + pageSize, filtered.length)}</strong> of <strong>{filtered.length}</strong>
          </span>
          <div className="dt-pager" role="navigation" aria-label="Pagination">
            <button onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
            {pages.map((n) => (
              <button
                key={n}
                aria-current={n === safePage ? "page" : undefined}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</button>
          </div>
        </div>
      )}
      {footer}
    </div>
  );
};

export default DataTable;
